import React, {useState} from 'react';

import PropTypes from 'prop-types';
import {Form, FormikProps} from 'formik';
import {CallMade} from '@material-ui/icons';
import {Box, Grid, Typography} from '@material-ui/core';

import {FieldShape} from 'commons/shapes';
import {InferPropTypes} from 'commons/types/Types';
import {IdentificationServiceRequest} from 'commons/models';
import {EfeButtonPrimary, EfeErrorMsg, EfeFormSection, EfeSupportMessage} from 'components';
import {Constants, DNI_BACK, DNI_FRONT, INE_ID, Messages} from 'commons/constants';
import EfeDragAndDrop from 'components/efe-drag-and-drop/EfeDragAndDrop';
import EfeWebCam from 'components/efe-webcam/EfeWebCam';
import {FileWithPath} from 'react-dropzone';
import CheckIcon from '@material-ui/icons/Check';

InnerOfficialId.propTypes = {
    foreign: PropTypes.bool.isRequired,
    fields: PropTypes.arrayOf(PropTypes.shape(FieldShape).isRequired).isRequired,
};

interface ComplexFile {
    file: FileWithPath;
    id: string;
}

function InnerOfficialId(
    props: InferPropTypes<
        typeof InnerOfficialId.propTypes & FormikProps<IdentificationServiceRequest>
    >
) {
    const {values, errors, fields, isSubmitting, setFieldValue, foreign} = props;
    const [captured, setCaptured] = useState<boolean>(true);
    const [subtitle, setSubtitle] = useState<string>('');
    const [uploadedFiles] = useState<{values: ComplexFile[]}>({values: []});
    const [len, setLen] = useState<number>(-1);
    const [title, setTitle] = useState<string>('');
    const [dniFrontConfirmed, setDniFrontConfirmed] = useState<boolean>(false);
    const [dniBackConfirmed, setDniBackConfirmed] = useState<boolean>(false);
    const [listActions, setListActions] = useState([
        {
            id: 'capture',
            class: 'button',
            name: 'Tomar foto',
            active: false,
            task: '',
        },
        {
            id: 'recapture',
            class: 'takeAgainButton',
            name: 'Tomar de nuevo',
            active: false,
            task: '',
        },
        {
            id: 'confirm',
            class: 'button',
            name: 'Confirmar',
            active: false,
            task: '',
        },
    ]);
    let dniType = '';

    function deleteFile(index: number) {
        if (uploadedFiles && uploadedFiles.values.length) {
            const aux = [...uploadedFiles.values];
            const id = aux[index].id;
            aux.splice(index, 1);
            uploadedFiles.values = aux;
            setLen(aux.length);
            if (id === DNI_FRONT) {
                setDniFront('');
            } else {
                setDniBack('');
            }
        }
    }
    function setDniFront(file: string | ArrayBuffer) {
        setFieldValue('dniFront', file);
    }
    function setDniBack(file: string | ArrayBuffer) {
        setFieldValue('dniBack', file);
    }

    function uploadFile(file: FileWithPath) {
        if (file && uploadedFiles && uploadedFiles.values) {
            const numFiles = dniType === INE_ID ? 2 : 1;
            if (uploadedFiles.values.length < numFiles) {
                const reader = new FileReader();
                const auxFile = {file: file, id: ''};
                if (uploadedFiles.values.length === 0) {
                    reader.onload = () => {
                        setDniFront(reader.result ? reader.result : '');
                        auxFile.id = DNI_FRONT;
                    };
                    reader.readAsDataURL(file);
                } else {
                    reader.onload = () => {
                        if (uploadedFiles.values[0].id === DNI_FRONT) {
                            setDniBack(reader.result ? reader.result : '');
                            auxFile.id = DNI_BACK;
                        } else {
                            setDniFront(reader.result ? reader.result : '');
                            auxFile.id = DNI_FRONT;
                        }
                    };
                    reader.readAsDataURL(file);
                }
                const aux = [...uploadedFiles.values];
                aux.push(auxFile);
                uploadedFiles.values = aux;
                setLen(aux.length);
            }
        }
    }

    function validateAction(id: string, task: string) {
        const copy = [...listActions];
        if (id === 'enableCapture') {
            setDniBackConfirmed(false);
            setDniFrontConfirmed(false);
            setSubtitle(dniType === '1' ? Messages.CAPTURE_DNI_FRONT : Messages.CAPTURE_DNI);
            setTitle(dniType === '1' ? Messages.CAPTURE_DNI_STEP_1 : Messages.CAPTURE_DNI_ONE_STEP);
            setCaptured(false);
            copy[0].active = true;
            copy[1].active = false;
            copy[2].active = false;
            copy[0].task = Constants.CAPTURE_DNI_FRONT;
        } else if (id === 'capture') {
            validateCapture(id, task);
        } else if (id === 'recapture') {
            validateRecapture(task);
        } else if (id === 'confirm') {
            validateConfirmation(task);
        }
        setListActions(copy);
    }

    function validateRecapture(task: string) {
        const copy = [...listActions];
        setCaptured(false);
        if (task === Constants.CAPTURE_DNI_FRONT) {
            copy[0].task = Constants.CAPTURE_DNI_FRONT;
        } else {
            copy[0].task = Constants.CAPTURE_DNI_BACK;
        }
        copy[0].active = true;
        copy[1].active = false;
        copy[2].active = false;
        setListActions(copy);
    }
    function validateConfirmation(task: string) {
        const copy = [...listActions];
        setSubtitle(Messages.CAPTURE_DNI_BACK);
        setTitle(Messages.CAPTURE_DNI_STEP_2);
        setCaptured(false);
        if (task === Constants.CONFIRM_DNI_FRONT) {
            copy[0].task = Constants.CAPTURE_DNI_BACK;
            copy[0].active = true;
            setDniFrontConfirmed(true);
        } else {
            copy[0].active = false;
            setDniBackConfirmed(true);
        }
        copy[1].active = false;
        copy[2].active = false;
        setListActions(copy);
    }
    function validateCapture(id: string, task: string) {
        const copy = [...listActions];
        setCaptured(true);
        copy[0].active = false;
        copy[1].active = true;
        copy[2].active = true;
        if (task === Constants.CAPTURE_DNI_FRONT) {
            copy[2].task = Constants.CONFIRM_DNI_FRONT;
        } else {
            copy[2].task = Constants.CONFIRM_DNI_BACK;
        }
        copy[0].task = '';
        setSubtitle('Confirmaci\u00F3n');
        setListActions(copy);
    }

    React.useEffect(() => {
        if (values.identificationType === INE_ID) {
            dniType = INE_ID;
            values.otherIdentificationType = INE_ID;
            values.otherIdentificationNumber = values.identificationNumber;
        } else {
            dniType = '';
        }
    }, [values.identificationType]);

    React.useEffect(() => {
        if (values.identificationType === INE_ID) {
            values.otherIdentificationNumber = values.identificationNumber;
        }
    }, [values.identificationNumber]);

    React.useEffect(() => {
        if (values && values.dniFront) {
            setDniFrontConfirmed(true);
        }
    }, [values.dniFront]);

    React.useEffect(() => {
        if (values && values.dniBack) {
            setDniBackConfirmed(true);
        }
    }, [values.dniBack]);

    const UploadFiles = (
        <>
            <Box mt={4} mb={4}>
                <Typography variant='body2' color='textPrimary'>
                    {dniType === '1'
                        ? Messages.UPLOAD_YOUR_IDENTIFICATION
                        : Messages.UPLOAD_YOUR_SINGLE_IDENTIFICATION}
                </Typography>
            </Box>
            <Grid container direction='row' justifyContent='flex-end'>
                <Grid item xs={6}>
                    <Box>
                        <EfeWebCam
                            title={title}
                            legend={Messages.AUTHORIZATION_TO_USE_CAMERA}
                            description={Messages.INFORMATION_VALIDATION}
                            subtitle={subtitle}
                            setDniFront={setDniFront}
                            setDniBack={setDniBack}
                            headerActions={listActions}
                            handleHeaderAction={validateAction}
                            captured={captured}
                            numberPhotos={dniType === '1' ? 2 : 1}
                        />
                    </Box>
                    <Box>
                        {dniFrontConfirmed && (
                            <p>
                                <CheckIcon
                                    fontSize='small'
                                    style={{
                                        color: 'green',
                                    }}></CheckIcon>
                                <span>{'Parte frontal'}</span>
                            </p>
                        )}
                        {dniBackConfirmed && (
                            <p>
                                <CheckIcon
                                    fontSize='small'
                                    style={{
                                        color: 'green',
                                    }}></CheckIcon>
                                <span>{'Parte trasera'}</span>
                            </p>
                        )}
                    </Box>
                </Grid>
                <Grid item xs={6}>
                    <Box>
                        <EfeDragAndDrop
                            maxSizeFile={10}
                            numFiles={3}
                            file={values.dniFront}
                            fileList={uploadedFiles.values.map((a) => a.file)}
                            deleteFile={deleteFile}
                            loadFile={uploadFile}
                            length={len}
                        />
                    </Box>
                </Grid>
            </Grid>
            {errors.dniFront && <EfeErrorMsg message={errors.dniFront} />}
            {errors.dniBack && <EfeErrorMsg message={errors.dniBack} />}
        </>
    );

    /* eslint-disable react/jsx-handler-names */
    return (
        <Form noValidate>
            {fields && fields.length > 0 && !foreign && (
                <Box m={2}>
                    <EfeSupportMessage />
                    <EfeFormSection
                        fields={fields}
                        values={values}
                        errors={errors}
                        setFieldValue={setFieldValue}
                    />
                    {!foreign && UploadFiles}
                    <Grid container direction='row' justifyContent='flex-end'>
                        <Box pt={6} width={160}>
                            <EfeButtonPrimary
                                fullWidth
                                disabled={isSubmitting}
                                type='submit'
                                endIcon={<CallMade />}>
                                {'OK'}
                            </EfeButtonPrimary>
                        </Box>
                    </Grid>
                </Box>
            )}
            {fields && fields.length > 0 && foreign && (
                <Box  m={2}>
                <EfeErrorMsg message={'Esta informac\u00F3n no aplica para el representante legal Extranjero'} />
                <Grid container direction='row' justifyContent='flex-end'>
                    <Box pt={6} width={160}>
                        <EfeButtonPrimary
                            fullWidth
                            disabled={isSubmitting}
                            type='submit'
                            endIcon={<CallMade />}>
                            {'OK'}
                        </EfeButtonPrimary>
                    </Box>
                </Grid>
            </Box>
            )}
        </Form>
    );
}

export default InnerOfficialId;
